import React, { useState, useEffect } from 'react'
import AdminLayout from '@/components/AdminLayout'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Plus,
  Search,
  User,
  LayoutGrid,
  ListEndIcon,
  ListIcon,
  FileSpreadsheet,
} from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { getPaginationPages, getTotalPages, paginate } from '@/utils/pagination'
import EmployeeAddModal from '@/components/EmployeeAddModal'
import EmployeeExcelUpdateModal from '@/components/EmployeeExcelUpdateModal'

interface Employee {
  id: string
  name: string
  email: string
  department: string
  role: string
  createdAt: string
  sicil?: string
  kod?: string
  magaza?: string
}

const AdminEmployees = () => {
  const [employees, setEmployees] = useState<Employee[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [addOpen, setAddOpen] = useState(false)
  const [excelUpdateOpen, setExcelUpdateOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card')
  const perPageOptions = viewMode === 'card' ? [6, 9, 15] : [10, 20, 50, 100]
  const [perPage, setPerPage] = useState(viewMode === 'card' ? 9 : 10)
  const [page, setPage] = useState(1)

  const navigate = useNavigate()

  // Fetch employees from Supabase
  const fetchEmployees = async () => {
    let allData: any[] = []
    let page = 0
    const pageSize = 1000

    try {
      while (true) {
        const { data, error } = await supabase
          .from('employee')
          .select('*')
          .range(page * pageSize, (page + 1) * pageSize - 1)

        if (error) {
          console.error('Error fetching employees:', error)
          break
        }

        if (!data || data.length === 0) break

        allData = [...allData, ...data]
        page++
      }
      return allData
    } catch (error) {
      console.error('Error in pagination:', error)
      return []
    }
  }

  // Transform Supabase data to match existing Employee type
  const transformSupabaseData = (supabaseData: any[]): Employee[] => {
    return supabaseData.map((item) => ({
      id: item.id,
      name: item.personel || 'N/A',
      email: item.mail_adresi || 'N/A',
      department: item.magaza || 'N/A',
      role: item.pozisyon || 'N/A',
      createdAt: item.created_at || new Date().toISOString(),
      sicil: item.sicil,
      kod: item.kod,
      magaza: item.magaza,
    }))
  }

  // Load employees on component mount
  useEffect(() => {
    const loadEmployees = async () => {
      setLoading(true)
      try {
        const supabaseData = await fetchEmployees()
        const transformedEmployees = transformSupabaseData(supabaseData)
        setEmployees(transformedEmployees)
      } catch (error) {
        console.error('Error loading employees:', error)
      } finally {
        setLoading(false)
      }
    }

    loadEmployees()
  }, [])

  const handleEmployeeAdded = (emp) => setEmployees((prev) => [emp, ...prev])

  // Filter employees based on search query
  const filteredEmployees = employees.filter(
    (employee) =>
      employee.name.toLocaleLowerCase('tr').includes(searchQuery.toLocaleLowerCase('tr')) ||
      employee.email.toLocaleLowerCase('tr').includes(searchQuery.toLocaleLowerCase('tr')) ||
      employee.department.toLocaleLowerCase('tr').includes(searchQuery.toLocaleLowerCase('tr')) ||
      employee.role.toLocaleLowerCase('tr').includes(searchQuery.toLocaleLowerCase('tr')) ||
      (employee.sicil && employee.sicil.toLocaleLowerCase('tr').includes(searchQuery.toLocaleLowerCase('tr'))) ||
      (employee.kod && employee.kod.toLocaleLowerCase('tr').includes(searchQuery.toLocaleLowerCase('tr')))
  )

  const formatDate = (isoDate: string) => {
    return new Date(isoDate).toLocaleDateString('tr-TR')
  }

  useEffect(() => {
    setPage(1)
    setPerPage(viewMode === 'card' ? 9 : 10)
  }, [viewMode])

  //Pagination
  const totalPages = getTotalPages(filteredEmployees.length, perPage)
  const paginatedEmployees = paginate(filteredEmployees, page, perPage)

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1 className="text-2xl font-bold">Çalışan Yönetimi</h1>
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2" onClick={() => setExcelUpdateOpen(true)}>
              <FileSpreadsheet className="w-4 h-4" />
              Çalışan Güncelle
            </Button>
            <Button variant="default" className="gap-2" onClick={() => setAddOpen(true)}>
              <Plus className="w-4 h-4" />
              Çalışan Ekle
            </Button>
          </div>
        </div>
        <EmployeeAddModal
          open={addOpen}
          setOpen={setAddOpen}
          onEmployeeAdded={handleEmployeeAdded}
        />
        <EmployeeExcelUpdateModal
          open={excelUpdateOpen}
          setOpen={setExcelUpdateOpen}
          onEmployeesUpdated={() => {
            // Reload employees after update
            const loadEmployees = async () => {
              setLoading(true)
              try {
                const supabaseData = await fetchEmployees()
                const transformedEmployees = transformSupabaseData(supabaseData)
                setEmployees(transformedEmployees)
              } catch (error) {
                console.error('Error loading employees:', error)
              } finally {
                setLoading(false)
              }
            }
            loadEmployees()
          }}
        />

        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
          <div className="flex items-center gap-2 w-full md:w-auto">
            <Search className="h-5 w-5 text-gray-400" />
            <Input
              placeholder="Çalışanları ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-md"
            />
          </div>
          <div className="flex items-center gap-2 justify-end w-full md:w-auto mt-2 md:mt-0">
            <Button
              variant={viewMode === 'card' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('card')}
            >
              <LayoutGrid className="h-4 w-4 mr-1" /> Kart
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <ListIcon className="h-4 w-4 mr-1" /> Liste
            </Button>
            <div className="flex items-center gap-2 ml-4">
              <span className="text-sm text-gray-500">Sayfa başı:</span>
              <Select
                value={perPage.toString()}
                onValueChange={(v) => {
                  setPerPage(Number(v))
                  setPage(1)
                }}
              >
                <SelectTrigger className="w-20">
                  <SelectValue placeholder="Sayfa başı" />
                </SelectTrigger>
                <SelectContent>
                  {perPageOptions.map((opt) => (
                    <SelectItem key={opt} value={opt.toString()}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {filteredEmployees.length > 0 ? (
          viewMode === 'card' ? (
            // CARD VIEW
            <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {paginatedEmployees.map((employee) => (
                <Card key={employee.id} className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="p-4 bg-gray-50 flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className="bg-gray-200 w-10 h-10 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-500" />
                        </div>
                        <div>
                          <h3 className="font-medium">{employee.name}</h3>
                          <p className="text-sm text-gray-500">{employee.email}</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-500">Mağaza</p>
                          <p>{employee.department}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Pozisyon</p>
                          <p>{employee.role}</p>
                        </div>
                      </div>
                      {employee.sicil && (
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-sm text-gray-500">Sicil No</p>
                            <p>{employee.sicil}</p>
                          </div>
                          {employee.kod && (
                            <div>
                              <p className="text-sm text-gray-500">Kod</p>
                              <p>{employee.kod}</p>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="mt-4">
                        <Link to={`/admin/employees/${employee.id}`}>
                          <Button variant="outline" className="w-full">
                            Detayları Görüntüle
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            // LIST VIEW
            <div className="overflow-x-auto">
              <div className="rounded-2xl shadow-lg border border-gray-200 bg-white overflow-hidden my-4">
                <table className="min-w-full bg-white">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-4 text-left rounded-tl-2xl">Adı</th>
                      <th className="p-4 text-left">Email</th>
                      <th className="p-4 text-left">Mağaza</th>
                      <th className="p-4 text-left">Pozisyon</th>
                      <th className="p-4 text-left">Sicil No</th>
                      <th className="p-4 text-left">Kod</th>
                      <th className="p-4 text-left">Oluşturulma</th>
                      <th className="p-4 rounded-tr-2xl"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedEmployees.map((employee, idx) => (
                      <tr
                        key={employee.id}
                        className={
                          'hover:bg-gray-50 cursor-pointer transition ' +
                          (idx === paginatedEmployees.length - 1 ? 'border-b-0' : '')
                        }
                        onClick={() => navigate(`/admin/employees/${employee.id}`)}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') navigate(`/admin/employees/${employee.id}`)
                        }}
                        style={{ userSelect: 'none' }}
                      >
                        <td
                          className={
                            'p-4 border-b border-gray-100 ' +
                            (idx === paginatedEmployees.length - 1 ? 'rounded-bl-2xl' : '')
                          }
                        >
                          {employee.name}
                        </td>
                        <td className="p-4 border-b border-gray-100">{employee.email}</td>
                        <td className="p-4 border-b border-gray-100">{employee.department}</td>
                        <td className="p-4 border-b border-gray-100">{employee.role}</td>
                        <td className="p-4 border-b border-gray-100">{employee.sicil || '-'}</td>
                        <td className="p-4 border-b border-gray-100">{employee.kod || '-'}</td>
                        <td className="p-4 border-b border-gray-100">
                          {formatDate(employee.createdAt)}
                        </td>
                        <td
                          className={
                            'p-4 border-b border-gray-100 flex gap-2 ' +
                            (idx === paginatedEmployees.length - 1 ? 'rounded-br-2xl' : '')
                          }
                        ></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">
              {searchQuery
                ? 'Arama kriterine uygun çalışan bulunamadı.'
                : 'Henüz çalışan bulunamadı.'}
            </p>
            {searchQuery && (
              <Button variant="outline" onClick={() => setSearchQuery('')}>
                Aramayı Temizle
              </Button>
            )}
          </div>
        )}

        {/* Pagination controls */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={page === 1}
            >
              Önceki
            </Button>
            {getPaginationPages(page, totalPages).map((pg, idx) =>
              pg === '...' ? (
                <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">
                  ...
                </span>
              ) : (
                <Button
                  key={pg + '-' + idx}
                  variant={page === pg ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPage(Number(pg))}
                >
                  {pg}
                </Button>
              )
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              disabled={page === totalPages}
            >
              Sonraki
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

export default AdminEmployees
